'use client';

import React, { useState, useEffect } from 'react';

import { format } from 'date-fns';
import { MessageCircle } from 'lucide-react';
import Head from 'next/head';
import Image from 'next/image';
import { JsonLd } from 'react-schemaorg';
import { BlogPosting, BreadcrumbList, WithContext } from 'schema-dts';

import AITravelPlannerPromo from '@/components/AITravelPlannerPromo';
import CommentForm from '@/components/blog/CommentForm';
import LikeButton from '@/components/blog/LikeButton';
import ReadingProgress from '@/components/blog/ReadingProgress';
import RelatedBlogPosts from '@/components/blog/RelatedBlogPosts';
import ShareButton from '@/components/blog/ShareButton';
import Breadcrumbs from '@/components/Breadcrumbs';
import JumpToTopButton from '@/components/JumpToTopButton';
import TableOfContents from '@/components/TableOfContents';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Post } from '@/types/wordpress';

interface BlogPostProps {
  post: Post;
  relatedPosts: Post[];
  category: {
    name: string;
    slug: string;
  };
}

const BlogPost: React.FC<BlogPostProps> = ({ post, relatedPosts = [], category }) => {
  const [comments, setComments] = useState<string[]>([]);
  const [readingTime, setReadingTime] = useState(0);

  useEffect(() => {
    const text = post.content.replace(/<[^>]*>/g, '');
    const wpm = 225;
    const words = text.trim().split(/\s+/).length;
    const time = Math.ceil(words / wpm);
    setReadingTime(time);
  }, [post.content]);

  const handleCommentSubmit = (comment: string) => {
    setComments(prevComments => [...prevComments, comment]);
  };

  const generateStructuredData = (
    post: Post,
    category: { name: string; slug: string }
  ): WithContext<BlogPosting | BreadcrumbList>[] => {
    if (post.rank_math_schema) {
      const schema = Array.isArray(post.rank_math_schema)
        ? post.rank_math_schema
        : [post.rank_math_schema];
      return schema as WithContext<BlogPosting | BreadcrumbList>[];
    }

    const blogPostingSchema: WithContext<BlogPosting> = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      image: post.featuredImage ? post.featuredImage.sourceUrl : undefined,
      datePublished: post.date,
      dateModified: post.modified,
      author: {
        '@type': 'Person',
        name: post.author.name,
      },
      publisher: {
        '@type': 'Organization',
        name: 'Destinations AI',
        logo: {
          '@type': 'ImageObject',
          url: 'https://destinations.ai/logo.png',
        },
      },
      description: post.excerpt || undefined,
    };

    const breadcrumbItems = [
      {
        '@type': 'ListItem' as const,
        position: 1,
        item: {
          '@type': 'Thing' as const,
          '@id': 'https://destinations.ai',
          name: 'Home',
        },
      },
      {
        '@type': 'ListItem' as const,
        position: 2,
        item: {
          '@type': 'Thing' as const,
          '@id': 'https://destinations.ai/blog',
          name: 'Blog',
        },
      },
      {
        '@type': 'ListItem' as const,
        position: 3,
        item: {
          '@type': 'Thing' as const,
          '@id': `https://destinations.ai/${category.slug}`,
          name: category.name,
        },
      },
      {
        '@type': 'ListItem' as const,
        position: 4,
        item: {
          '@type': 'Thing' as const,
          '@id': `https://destinations.ai/blog/${post.slug}`,
          name: post.title,
        },
      },
    ];

    const breadcrumbSchema: WithContext<BreadcrumbList> = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbItems,
    };

    return [blogPostingSchema, breadcrumbSchema];
  };

  const processContent = (content: string) => {
    if (typeof window === 'undefined') return content; // Return unprocessed content on server-side

    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');

    // Process headings
    const headings = doc.querySelectorAll('h2');
    headings.forEach((heading, index) => {
      heading.id = `heading-${index}`;
    });

    // Process images
    const images = doc.querySelectorAll('img');
    images.forEach(img => {
      const figure = doc.createElement('figure');
      figure.className = 'my-8';
      const newImg = doc.createElement('img');
      newImg.src = img.src;
      newImg.alt = img.alt;
      newImg.className = 'w-full h-auto rounded-lg';
      figure.appendChild(newImg);
      if (img.nextElementSibling && img.nextElementSibling.tagName === 'FIGCAPTION') {
        const figcaption = img.nextElementSibling.cloneNode(true) as HTMLElement;
        figcaption.className = 'text-sm text-center mt-2 text-muted-foreground';
        figure.appendChild(figcaption);
        img.nextElementSibling.remove();
      }
      if (img.parentNode) {
        img.parentNode.replaceChild(figure, img);
      }
    });

    return doc.body.innerHTML;
  };

  return (
    <>
      <Head>
        <title>{post.title} | Destinations AI Blog</title>
        <meta name="description" content={post.excerpt || ''} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt || ''} />
        <meta property="og:image" content={post.featuredImage?.sourceUrl || ''} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt || ''} />
        <meta name="twitter:image" content={post.featuredImage?.sourceUrl || ''} />
      </Head>
      {generateStructuredData(post, category).map((schema, index) => (
        <JsonLd key={index} item={schema} />
      ))}
      <ReadingProgress />
      <article className="max-w-4xl mx-auto bg-background text-foreground">
        <Breadcrumbs
          items={[
            { label: 'Home', href: '/' },
            { label: 'Blog', href: '/blog' },
            { label: post.title, href: `/blog/${post.slug}` },
          ]}
        />
        <header className="mb-8">
          <h1
            className="text-3xl sm:text-4xl font-bold mb-4 text-primary"
            dangerouslySetInnerHTML={{ __html: post.title }}
          />
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
            <div className="flex items-center mb-2 sm:mb-0">
              <Avatar className="mr-2">
                <AvatarImage src={post.author.avatar} alt={post.author.name} />
                <AvatarFallback>{post.author.name.charAt(0)}</AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <span className="text-sm text-muted-foreground">By {post.author.name}</span>
                <span className="text-xs text-muted-foreground">
                  Published: {format(new Date(post.date), 'MMMM dd, yyyy')}
                </span>
                <span className="text-xs text-muted-foreground">
                  Last modified: {format(new Date(post.modified), 'MMMM dd, yyyy')}
                </span>
              </div>
            </div>
            <span className="text-sm text-muted-foreground">{readingTime} min read</span>
          </div>
        </header>

        {post.featuredImage && (
          <div className="relative w-full h-[50vh] mb-8">
            <Image
              src={post.featuredImage.sourceUrl}
              alt={post.featuredImage.altText}
              fill
              className="rounded-lg object-cover w-full h-full"
            />
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-8">
          <div className="order-2 lg:order-1 lg:w-3/4">
            <div
              className="prose prose-lg max-w-none mb-8 dark:prose-invert"
              dangerouslySetInnerHTML={{ __html: processContent(post.content) }}
            />

            <AITravelPlannerPromo />

            <div className="flex justify-between items-center mb-8">
              <div className="flex space-x-4">
                <LikeButton postId={post.id} initialLikes={0} />
                <Button variant="outline" size="sm">
                  <MessageCircle className="mr-2 h-4 w-4" /> Comments
                </Button>
                <ShareButton title={post.title} url={`https://destinations.ai/blog/${post.slug}`} />
              </div>
            </div>

            <CommentForm postId={post.id} onCommentSubmit={handleCommentSubmit} />

            <div className="mt-8">
              <h2 className="text-2xl font-bold mb-4 text-primary">Comments</h2>
              {comments.map((comment, index) => (
                <div key={index} className="bg-muted p-4 rounded-lg mb-4">
                  <p className="text-foreground">{comment}</p>
                </div>
              ))}
            </div>
            <RelatedBlogPosts posts={relatedPosts} currentPostId={post.id} />
          </div>
          <div className="order-1 lg:order-2 lg:w-1/4">
            <div className="lg:sticky lg:top-4">
              <TableOfContents
                title="Table of Contents"
                onLinkClick={id => {
                  const element = document.getElementById(id);
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              />
              <AITravelPlannerPromo />
            </div>
          </div>
        </div>
      </article>
      <JumpToTopButton />
    </>
  );
};

export default BlogPost;
